import { ColumnDef, Getter, Row } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useCallback, useMemo, useRef } from 'react';
import {
  Avatar,
  AvatarGroup,
  Badge,
  CustomCellContext,
  Icon,
  Icons,
  SortableHeader,
  Typography,
  TypographyType,
  UserBadge,
} from '../../../components';
import { PROSPECT_CATEGORY_TO_COLOR, PROSPECT_TYPE_TO_LABEL } from '../../../constants';
import { useAppSelector, useDetectTextOverflow } from '../../../hooks';
import {
  Call,
  CallProcessingStatus,
  CallSortingFilters,
  ComponentSize,
  CustomSortingState,
  DateFormat,
  PracticeProspect,
  RealCall,
  Roles,
  SimulationCall,
  TextColor,
  TimeFormat,
} from '../../../types';
import {
  conditionalArray,
  formatSecondsToDuration,
  getCallerInfo,
  getTooltipWithPlacement,
  isCallInProgress,
  isCallWithoutDuration,
  snakeCaseToLabel,
} from '../../../utils';
import FlaggedIndicator from './FlaggedIndicator';
import ReviewListenCell from './ReviewListenCell';

interface UseReviewTabColumnsProps {
  sorting: CustomSortingState;
  setSorting: React.Dispatch<React.SetStateAction<CustomSortingState>>;
}

const useReviewTabColumns = ({ sorting, setSorting }: UseReviewTabColumnsProps) => {
  const user = useAppSelector((state) => state.auth.user);
  const calls = useAppSelector((state) => state.review.calls);
  const isSalesRep = useMemo(() => user?.role === Roles.SALES_REP, [user?.role]);

  const showCommentsColumn = calls.some((call) => !!call.numberOfComments);

  const renderSortableHeader = useCallback(
    (sortingId: CallSortingFilters, title: string) => (
      <SortableHeader title={title} sorting={sorting} setSorting={setSorting} sortingId={sortingId} />
    ),
    [sorting, setSorting]
  );

  const isFlaggedColumn: ColumnDef<Call> = useMemo(
    () => ({
      accessorKey: 'isFlagged',
      header: '',
      cell: ({ row, isActive, isHovered }: CustomCellContext<Call, void>) => {
        const { isFlaggedToUser, flaggedUserIds } = row.original;
        const isFlagged = !!isFlaggedToUser || !!flaggedUserIds?.length;
        return isFlagged ? (
          <FlaggedIndicator call={row.original} isActive={!!isActive || !!isHovered} />
        ) : (
          // Render an empty div with a width of 8px to maintain column width if no call is flagged.
          <div className="w-2" />
        );
      },
    }),
    []
  );

  const listenColumn: ColumnDef<Call> = useMemo(
    () => ({
      accessorKey: 'listen',
      header: '',
      cell: ({ row }: { row: Row<Call> }) => (
        <div className="flex w-10 justify-center">
          <ReviewListenCell call={row.original} />
        </div>
      ),
    }),
    []
  );

  const callDurationColumn: ColumnDef<Call> = useMemo(
    () => ({
      accessorKey: 'callDuration',
      header: () => renderSortableHeader(CallSortingFilters.CALL_DURATION, 'Duration'),
      cell: ({ row }) => {
        // Hide duration if call is live or creation failed.
        if (isCallInProgress(row.original) || isCallWithoutDuration(row.original)) {
          return null;
        }

        const duration = row.original.callDuration || 0;
        return formatSecondsToDuration(duration);
      },
    }),
    []
  );

  const callerColumn: ColumnDef<Call> = useMemo(
    () => ({
      accessorKey: 'user',
      header: 'User',
      size: 1 / 2,
      cell: ({ row }: { row: Row<Call> }) => {
        if (!user) return 'N/A';
        const call = row.original;
        const caller = getCallerInfo(call, user);
        if (!caller) return 'N/A';

        const { name, picture } = caller;
        return <UserBadge title={name} imgSrc={picture} hideAvatarIfNoImgSrc />;
      },
    }),
    []
  );

  const commentsColumn: ColumnDef<Call> = useMemo(
    () => ({
      accessorKey: 'numberOfComments',
      header: () => 'Comments',
      cell: ({ row }: { row: Row<Call> }) => {
        const { numberOfComments, processingStatus, usersCommented = [] } = row.original;
        const isCallProcessed = processingStatus === CallProcessingStatus.PROCESSED;

        // Hide comments if call hasn't finished processing.
        if (!isCallProcessed || !numberOfComments) {
          return null;
        }

        return (
          <div className="flex items-center gap-1">
            <Icons icon={Icon.COMMENT} color={TextColor.TERTIARY} />
            <Typography type={TypographyType.X_SMALL} color={TextColor.SECONDARY}>
              {numberOfComments}
            </Typography>
            <AvatarGroup className="!-space-x-2.5" size={20}>
              {usersCommented.map((user) => (
                <Avatar label={user.name} key={user.id} imgSrc={user.picture} showTooltip />
              ))}
            </AvatarGroup>
          </div>
        );
      },
    }),
    []
  );

  const scorecardColumn: ColumnDef<Call> = useMemo(
    () => ({
      accessorKey: 'scorecard',
      header: () => renderSortableHeader(CallSortingFilters.POINTS_PERCENTAGE, 'Score'),
      cell: ({ row }) => {
        const { processingStatus, scorecard } = row.original;
        const isCallProcessed = processingStatus === CallProcessingStatus.PROCESSED;
        const pointsPercentage = scorecard?.pointsPercentage;

        // Hide score if call hasn't finished processing.
        if (!isCallProcessed || typeof pointsPercentage !== 'number') {
          return null;
        }

        return `${Math.round(pointsPercentage * 100)}%`;
      },
    }),
    []
  );

  const startTimeColumn: ColumnDef<Call> = useMemo(
    () => ({
      accessorKey: 'startTime',
      header: () => renderSortableHeader(CallSortingFilters.START_TIME, 'Call date'),
      cell: ({ getValue }: { getValue: Getter<Date> }) => {
        const dateStr = getValue();
        return (
          <div className="flex flex-col gap-1">
            <Typography color={TextColor.TERTIARY} type={TypographyType.X_SMALL}>
              {dayjs(dateStr).format(TimeFormat.SHORT_TIME)}
            </Typography>
            <Typography color={TextColor.SECONDARY} type={TypographyType.X_SMALL}>
              {dayjs(dateStr).format(DateFormat.MONTH_DAY)}
            </Typography>
          </div>
        );
      },
    }),
    []
  );

  const practiceProspectColumn: ColumnDef<Call> = useMemo(
    () => ({
      accessorKey: 'practiceProspect',
      header: 'Simulation',
      size: 1 / 2,
      cell: ({ getValue }: { getValue: Getter<PracticeProspect> }) => {
        const prospect = getValue();

        const { firstName, lastName, jobTitle, accountName } = prospect || {};
        const title = `${firstName} ${lastName}`;
        const subtitle = `${accountName} | ${jobTitle}`;
        return <UserBadge title={title} subtitle={subtitle} />;
      },
    }),
    []
  );

  const categoryColumn: ColumnDef<Call> = useMemo(
    () => ({
      accessorKey: 'callType',
      header: 'Call type',
      cell: ({ row }) => {
        const call = row.original as SimulationCall;
        const { practiceProspect } = call;
        const { category, type } = practiceProspect || {};
        return (
          <div className="flex flex-col gap-1">
            <Badge
              color={PROSPECT_CATEGORY_TO_COLOR[category]}
              label={snakeCaseToLabel(category)}
              size={ComponentSize.MEDIUM}
            />
            <Typography type={TypographyType.X_SMALL} color={TextColor.SECONDARY} noWrap>
              {type ? PROSPECT_TYPE_TO_LABEL[type] : undefined}
            </Typography>
          </div>
        );
      },
    }),
    []
  );

  const meetingNameColumn: ColumnDef<Call> = useMemo(
    () => ({
      accessorKey: 'name',
      header: 'Meeting name',
      size: 1 / 2,
      cell: ({ row }) => {
        const call = row.original as RealCall;
        const singleLine = 1;
        const textRef = useRef<HTMLDivElement>(null);
        const isOverflowing = useDetectTextOverflow(textRef, singleLine);

        return (
          <Typography
            ref={textRef}
            maxLines={singleLine}
            tooltip={isOverflowing ? getTooltipWithPlacement(call.name) : undefined}
          >
            {call.name}
          </Typography>
        );
      },
    }),
    []
  );

  const simulationColumns: ColumnDef<Call>[] = useMemo(
    () => [
      isFlaggedColumn,
      listenColumn,
      callDurationColumn,
      ...conditionalArray(!isSalesRep, callerColumn),
      practiceProspectColumn,
      ...conditionalArray(showCommentsColumn, commentsColumn),
      scorecardColumn,
      categoryColumn,
      startTimeColumn,
    ],
    [
      isSalesRep,
      isFlaggedColumn,
      listenColumn,
      callDurationColumn,
      callerColumn,
      practiceProspectColumn,
      commentsColumn,
      showCommentsColumn,
      scorecardColumn,
      categoryColumn,
      startTimeColumn,
    ]
  );

  const realCallsColumns: ColumnDef<Call>[] = useMemo(
    () => [
      isFlaggedColumn,
      listenColumn,
      callDurationColumn,
      ...conditionalArray(!isSalesRep, callerColumn),
      meetingNameColumn,
      ...conditionalArray(showCommentsColumn, commentsColumn),
      scorecardColumn,
      startTimeColumn,
    ],
    [
      isSalesRep,
      isFlaggedColumn,
      listenColumn,
      callDurationColumn,
      callerColumn,
      meetingNameColumn,
      commentsColumn,
      showCommentsColumn,
      scorecardColumn,
      startTimeColumn,
    ]
  );

  return { simulationColumns, realCallsColumns };
};

export default useReviewTabColumns;
